import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Zap, 
  Plus, 
  Edit2, 
  Trash2, 
  X, 
  Search,
  Check,
  Tag
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuickReplyTemplates = ({ onSelectTemplate, compact = false }) => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Form states
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formCategory, setFormCategory] = useState('general');
  const [formShortcut, setFormShortcut] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setTemplates(response.data.templates);
        setCategories(response.data.categories || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    // Track usage
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/templates/${template.id}/use`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      console.error('Failed to track template usage:', e);
    }
    
    onSelectTemplate(template.content);
    setIsPopoverOpen(false);
  };

  const handleCreateTemplate = async () => {
    if (!formTitle.trim() || !formContent.trim()) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/templates`,
        {
          title: formTitle,
          content: formContent,
          category: formCategory,
          shortcut: formShortcut || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowCreateDialog(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('Failed to create template:', error);
      alert(error.response?.data?.detail || 'Gagal membuat template');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate || !formTitle.trim() || !formContent.trim()) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/templates/${editingTemplate.id}`,
        {
          title: formTitle,
          content: formContent,
          category: formCategory,
          shortcut: formShortcut || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowEditDialog(false);
      setEditingTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('Failed to update template:', error);
      alert(error.response?.data?.detail || 'Gagal memperbarui template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Hapus template ini?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormTitle(template.title);
    setFormContent(template.content);
    setFormCategory(template.category);
    setFormShortcut(template.shortcut || '');
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormTitle('');
    setFormContent('');
    setFormCategory('general');
    setFormShortcut('');
  };

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = !searchQuery || 
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.shortcut && t.shortcut.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || t.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const getCategoryColor = (category) => {
    const colors = {
      'general': 'bg-gray-100 text-gray-800',
      'greeting': 'bg-green-100 text-green-800',
      'closing': 'bg-indigo-100 text-blue-800',
      'faq': 'bg-purple-100 text-purple-800',
      'promotion': 'bg-orange-100 text-orange-800'
    };
    return colors[category] || colors.general;
  };

  return (
    <>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size={compact ? "icon" : "default"}
            title="Quick Reply Templates"
          >
            <Zap className="w-4 h-4" />
            {!compact && <span className="ml-2 hidden sm:inline">Templates</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <div className="p-3 border-b">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-sm">Quick Reply Templates</h4>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => {
                  resetForm();
                  setShowCreateDialog(true);
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                Baru
              </Button>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-400" />
              <Input
                placeholder="Cari template..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
            
            {/* Category filter */}
            {categories.length > 0 && (
              <div className="flex gap-1 mt-2 flex-wrap">
                <Badge 
                  variant={selectedCategory === 'all' ? 'default' : 'outline'}
                  className="cursor-pointer text-xs"
                  onClick={() => setSelectedCategory('all')}
                >
                  Semua
                </Badge>
                {categories.map(cat => (
                  <Badge 
                    key={cat}
                    variant={selectedCategory === cat ? 'default' : 'outline'}
                    className="cursor-pointer text-xs capitalize"
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          {/* Templates list */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-sm text-slate-500 dark:text-zinc-400">
                Loading...
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500 dark:text-zinc-400">
                {templates.length === 0 ? (
                  <>
                    Belum ada template.
                    <Button 
                      variant="link" 
                      size="sm"
                      onClick={() => {
                        resetForm();
                        setShowCreateDialog(true);
                      }}
                    >
                      Buat sekarang
                    </Button>
                  </>
                ) : (
                  'Tidak ada template yang cocok'
                )}
              </div>
            ) : (
              filteredTemplates.map(template => (
                <div 
                  key={template.id}
                  className="p-3 border-b hover:bg-slate-50 dark:hover:bg-zinc-900 cursor-pointer group dark:bg-zinc-900"
                >
                  <div className="flex items-start justify-between">
                    <div 
                      className="flex-1 min-w-0"
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm truncate">
                          {template.title}
                        </span>
                        {template.shortcut && (
                          <code className="text-xs bg-slate-100 px-1 rounded dark:bg-zinc-800">
                            {template.shortcut}
                          </code>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2 dark:text-zinc-400">
                        {template.content}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`text-xs ${getCategoryColor(template.category)}`}>
                          {template.category}
                        </Badge>
                        {template.usage_count > 0 && (
                          <span className="text-xs text-slate-500 dark:text-zinc-400">
                            {template.usage_count}x digunakan
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                      <Button 
                        size="icon" 
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(template);
                        }}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost"
                        className="h-6 w-6 text-red-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTemplate(template.id);
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Buat Template Baru</DialogTitle>
            <DialogDescription>
              Buat template balasan cepat untuk mempercepat respon Anda.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Judul</label>
              <Input
                placeholder="Contoh: Salam Pembuka"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Isi Pesan</label>
              <Textarea
                placeholder="Ketik isi pesan template..."
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                rows={4}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Kategori</label>
                <Select value={formCategory} onValueChange={setFormCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih kategori" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Umum</SelectItem>
                    <SelectItem value="greeting">Salam</SelectItem>
                    <SelectItem value="closing">Penutup</SelectItem>
                    <SelectItem value="faq">FAQ</SelectItem>
                    <SelectItem value="promotion">Promosi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium">Shortcut (Opsional)</label>
                <Input
                  placeholder="/salam"
                  value={formShortcut}
                  onChange={(e) => setFormShortcut(e.target.value)}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Batal
            </Button>
            <Button 
              onClick={handleCreateTemplate} 
              disabled={saving || !formTitle.trim() || !formContent.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {saving ? 'Menyimpan...' : 'Simpan Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Judul</label>
              <Input
                placeholder="Contoh: Salam Pembuka"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Isi Pesan</label>
              <Textarea
                placeholder="Ketik isi pesan template..."
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                rows={4}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Kategori</label>
                <Select value={formCategory} onValueChange={setFormCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih kategori" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Umum</SelectItem>
                    <SelectItem value="greeting">Salam</SelectItem>
                    <SelectItem value="closing">Penutup</SelectItem>
                    <SelectItem value="faq">FAQ</SelectItem>
                    <SelectItem value="promotion">Promosi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium">Shortcut (Opsional)</label>
                <Input
                  placeholder="/salam"
                  value={formShortcut}
                  onChange={(e) => setFormShortcut(e.target.value)}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Batal
            </Button>
            <Button 
              onClick={handleUpdateTemplate} 
              disabled={saving || !formTitle.trim() || !formContent.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickReplyTemplates;
